import React, { useContext } from 'react';
import { Link, useLoaderData, useSearchParams, Outlet, redirect } from 'react-router-dom';
import { ContentFrame } from '../../components/content_frame/content_frame';
import { AuthProvider } from '../../auth_provider';
import { COMPLAINT_CASE_LIST_PATH, 
         COMPLAINT_CASE_ADD_PATH, 
         COMPLAINT_CASE_EDIT_PATH, 
         DEFAULT_DELETE_PATH, 
         DESKTOP_PATH,
         LOGIN_PATH } from '../../constants/paths';
import { CLINICS_GET_API, PATIENTS_GET_API, COMPLAINT_CASES_GET_API } from '../../constants/apis';
import DefaultInput, { HalfWrapper, SelectOption } from '../../components/default_input/default_input';
import { AuthContext, ClinicListContext, PatientListContext } from '../../contexts';
import ModelTable, { Property } from '../../components/model_table/model_table';
import DefaultSection from '../../components/default_section/default_section';
import { getModels, getAsLocalDatetime, setUrlParameters } from '../../utils/functions';
import { VIEW_BASIC_COMPLAINT_CASE_PERMISSION, 
         ADD_COMPLAINT_CASE_PERMISSION, 
         DELETE_COMPLAINT_CASE_PERMISSION } from '../../constants/permissions';
import { COMPLAINT_CASE_STATUS_OPTIONS } from '../../constants/constants';
import './complaint_case_list.scss';


export async function complaintCaseListRootLoader() {
  try {
    let clinics;
    let patients;

    if (AuthProvider.is_authenticated) {
      clinics = getModels(setUrlParameters(CLINICS_GET_API, {is_active: true}));
      patients = getModels(setUrlParameters(PATIENTS_GET_API, {is_active: true}));
  
      patients = await patients;
      clinics = await clinics;      
    }
    else {
      clinics = await getModels(setUrlParameters(CLINICS_GET_API, {is_active: true}));
      patients = await getModels(setUrlParameters(PATIENTS_GET_API, {is_active: true}));     
    }

    return {clinics, patients};
  }
  catch(errors) {
    if(errors instanceof Array) {
      for(let error of errors) {
        if(error.code === 209) {
          // Expired user session
          return redirect(LOGIN_PATH);
        }
        else if(error.code === 203) {
          // Not authorized
          return redirect(DESKTOP_PATH);
        }
      }
    }

    throw errors;    
  }
};

export const ComplaintCaseListRoot = props => {
  const {clinics, patients} = useLoaderData();  

  return (
    <ClinicListContext.Provider value={clinics}>

      <PatientListContext.Provider value={patients}>

        <Outlet />

      </PatientListContext.Provider>

    </ClinicListContext.Provider>
  );
};

export async function complaintCaseListLoader({request}) { 
  const url = new URL(request.url);

  const statusIndex = url.searchParams.get("status");

  let has_finished = null;
  let form_filled = null;

  if (statusIndex) {
    switch (parseInt(statusIndex)) {
      case 0:
        has_finished = true;
        form_filled = true;        
        break;    
      case 1:
        has_finished = true;
        form_filled = false;        
        break;    
      case 2:
        has_finished = false;
        form_filled = true;        
        break;    
      case 3:
        has_finished = false;
        form_filled = false;        
        break;    
      default:
        break;
    }
  } 
  
  const parameters = {
    clinic_id: url.searchParams.get("clinic_id"),
    patient_id: url.searchParams.get("patient_id"),
    has_finished,
    form_filled,
    load_clinic_name: true,
    load_patient_name: true
  };

  try {
    return await getModels(setUrlParameters(COMPLAINT_CASES_GET_API, parameters));
  }
  catch(errors) {
    if(errors instanceof Array) {
      for(let error of errors) {
        if(error.code === 209) {
          // Expired user session
          return redirect(LOGIN_PATH);
        }
        else if(error.code === 203) {
          // Not authorized
          return redirect(DESKTOP_PATH);
        }
      }
    }

    throw errors;    
  }
};

class ComplaintCaseList extends React.Component {
  constructor(props) {
    super(props);
    
    let clinicIdInput = this.props.searchParams.get('clinic_id');
    let patientIdInput = this.props.searchParams.get('patient_id');
    let statusInput = this.props.searchParams.get('status');

    if(!clinicIdInput) {
      clinicIdInput = '';
    }
    if(!patientIdInput) {
      patientIdInput = '';
    }
    if(!statusInput) {
      statusInput = '';
    }

    this.state = {
      clinicIdInput: clinicIdInput,
      clinicIdFilter: clinicIdInput,
      patientIdInput: patientIdInput,
      patientIdFilter: patientIdInput,
      statusInput: statusInput,
      statusFilter: statusInput,
      screenWidth: window.innerWidth
    };
  }

  async componentDidMount() {
    this.resizeListener = () => this.updateSize();

    window.addEventListener("resize", this.resizeListener);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.resizeListener);
  }

  updateSize() {
    this.setState({
      screenWidth: window.innerWidth
    });
  }

  getCreatedAtText(entry) {
    return (getAsLocalDatetime(entry.created_at, false).toLocaleDateString())
  }

  listHasActions() {
    return this.props.auth.permission_ids.includes(VIEW_BASIC_COMPLAINT_CASE_PERMISSION) ||
           this.props.auth.permission_ids.includes(DELETE_COMPLAINT_CASE_PERMISSION);
  }

  getActions(entry) {
    return (
      <div className="model-table__model-actions-container">
      
        {this.props.auth.permission_ids.includes(VIEW_BASIC_COMPLAINT_CASE_PERMISSION) &&
          <Link
            className="model-table__default-edit-button"
            to={`${COMPLAINT_CASE_EDIT_PATH}${entry.id}`}
          >

              <i className="fas fa-edit"></i>

          </Link>
        }
        
        {this.props.auth.permission_ids.includes(DELETE_COMPLAINT_CASE_PERMISSION) &&
          <Link
            className="model-table__default-delete-button"
            to={`${DEFAULT_DELETE_PATH.slice(1)}${entry.id}`}
          >

              <i className="far fa-trash-alt"></i>

          </Link>
        }

      </div>
    );
  }

  getStatusIndex(complaint_case) {
    let index = 0;

    if (complaint_case.has_finished) {
      index = complaint_case.form_filled ? 0 : 1;
    }
    else {
      index = complaint_case.form_filled ? 2 : 3;
    }
    
    return index;
  }

  getStatusFilterText(entry) {
    return COMPLAINT_CASE_STATUS_OPTIONS[this.getStatusIndex(entry)];
  }

  getStatusText(entry) {
    let statusText;
    let classModifier;

    const statusIndex = this.getStatusIndex(entry);

    statusText = COMPLAINT_CASE_STATUS_OPTIONS[statusIndex];

    switch (statusIndex) {
      case 0:
      case 1:
        classModifier = '--finished';
        break;    
      case 2:
        classModifier = '--filled';
        break;    
      case 3:
        classModifier = '--awaiting';
        break;    
      default:
        statusText = 'INDEFINIDO'
        classModifier = '--awaiting';
        break;
    }

    return (
      <p className={`complaint-case-list__status-text${classModifier}`}>{statusText}</p>
    );
  }

  getProperties() {
    const properties = [
      Property('id', 'id', <i className="fa-solid fa-tag"></i>),
      Property('clinic_name', 'Cliente', <i className="fa-solid fa-tooth"></i>),
      Property('patient_name', 'Paciente', <i className="fa-solid fa-hospital-user"></i>),
      Property('created_at', 'Cadastro', <i className="fas fa-calendar-alt"></i>, {
        getDataText: this.getCreatedAtText, 
        getFilterText: this.getCreatedAtText}),
      Property('status', 'Situação', <i className="fas fa-thermometer-half"></i>, {
        getDataText: (entry) => this.getStatusText(entry), 
        getFilterText: (entry) => this.getStatusFilterText(entry),
        getSortCallback: (a, b) => {
          let aWeight = this.getStatusIndex(a);
          let bWeight = this.getStatusIndex(b);

          if (aWeight === bWeight) {
            return b.id - a.id; // Older entries comes first
          }
          
          return aWeight - bWeight;
        }})
    ];

    return properties;
  }

  getClinicOptions() {
    return [
      SelectOption('', 'Todas'),
      ...this.props.clinics.map((clinic) => SelectOption(clinic.id, clinic.name))
    ];
  }

  getPatientOptions() {
    if (this.state.clinicIdInput.length <= 0) {
      return [SelectOption('', 'Todos')]
    }
    
    return [
      SelectOption('', 'Todos'),
      ...this.props.patients.filter((patient) => patient.clinic_id === parseInt(this.state.clinicIdInput)).map((patient) => SelectOption(patient.id, patient.name))
    ];
  }

  getStatusOptions() {    
    return [
      SelectOption('', 'Todas'),
      ...COMPLAINT_CASE_STATUS_OPTIONS.map((entry, index) => SelectOption(index, entry))
    ];
  }

  mayResetFilterInputs() {
    if(this.state.clinicIdInput.length > 0 || 
       this.state.patientIdInput.length > 0 ||
       this.state.statusInput.length > 0) {
      return true;
    }

    return false;
  }

  resetFilterInputs() {
    this.setState({
      clinicIdInput: '',
      patientIdInput: '',
      statusInput: ''
    });
  }

  mayRefreshList() {
    if(this.state.clinicIdInput !== this.state.clinicIdFilter || 
       this.state.patientIdInput !== this.state.patientIdFilter ||
       this.state.statusInput !== this.state.statusFilter) {
      return true;
    }

    return false;
  }

  async refreshList() {
    if(this.mayRefreshList()) {
      
      this.props.setSearchParams(setUrlParameters("", {
        clinic_id: this.state.clinicIdInput,
        patient_id: this.state.patientIdInput,
        status: this.state.statusInput
      }), {
        replace: true
      });      

      this.setState({
        clinicIdFilter: this.state.clinicIdInput,
        patientIdFilter: this.state.patientIdInput,
        statusFilter: this.state.statusInput
      });
    }
  }

  handleInputChange(event) {
    const target = event.target;
    let value = target.value;
    let name = target.name;

    const update = {[name]: value};

    if (name === 'clinicIdInput' && this.state.patientIdInput.length > 0) {
      if (value.length > 0) {
        const clinic_id = parseInt(value);
        const patient = this.props.patients.find((patient) => patient.id === parseInt(this.state.patientIdInput));
        
        if (patient.clinic_id !== clinic_id) {
          update.patientIdInput = '';
        }
      }
      else {
        update.patientIdInput = '';
      }
    }

    this.setState(update);
  }

  getPatientInputLabelMessage() {
    if (this.state.clinicIdInput.length <= 0) {
      return 'Selecione um cliente para atualizar a lista';
    }
    
    return 'Lista de cadastros ativos';
  }

  render() {
    return (
      <>
        
        <Outlet />

        <ContentFrame
          headerHistory={[
            {
              path: DESKTOP_PATH,
              text: "Área de trabalho"
            },
            {
              path: COMPLAINT_CASE_LIST_PATH,
              text: "Listar casos"
            },
          ]}
          titleIcon={<i className="fas fa-clipboard-list"></i>}
          title="Listar casos"
        >

          <DefaultSection
            className="complaint-case-list"
            title="Lista de casos"
          >

            <div className="complaint-case-list__list-actions">

              {this.props.auth.permission_ids.includes(ADD_COMPLAINT_CASE_PERMISSION) &&
                <Link
                  className="model-table__default-button"
                  to={COMPLAINT_CASE_ADD_PATH}
                >

                  <i className="fas fa-plus"></i> Adicionar caso

                </Link>
              }

            </div>

            <div className="complaint-case-list__filters">

              <header className="complaint-case-list__filters__header">

                <h4 className="complaint-case-list__filters__header__text">Filtros</h4>

              </header>

              <div className="complaint-case-list__filters__inputs">

                <div className="complaint-case-list__filters__inputs-wrapper">

                  <HalfWrapper>

                    <DefaultInput
                      name="clinicIdInput"
                      label="Clínica"
                      labelMessage="Lista de cadastros ativos"
                      type="select"
                      handleInputChange={(event) => this.handleInputChange(event)}
                      value={this.state.clinicIdInput || ''}
                      options={this.getClinicOptions()}
                    />

                    <DefaultInput
                      name="patientIdInput"
                      label="Paciente"
                      labelMessage={this.getPatientInputLabelMessage()}
                      type="select"
                      handleInputChange={(event) => this.handleInputChange(event)}
                      value={this.state.patientIdInput || ''}
                      options={this.getPatientOptions()}
                    />

                  </HalfWrapper>

                  <DefaultInput
                    name="statusInput"
                    label="Situação"
                    type="select"
                    handleInputChange={(event) => this.handleInputChange(event)}
                    value={this.state.statusInput || ''}
                    options={this.getStatusOptions()}
                  />

                </div>

                {this.mayResetFilterInputs() &&
                  <button
                    className="complaint-case-list__filters__reset-button"
                    onClick={() => this.resetFilterInputs()}
                  >

                    <i className="fas fa-times"></i>

                  </button>
                }

                {this.mayRefreshList() &&
                  <button
                    className="complaint-case-list__filters__refresh-button"
                    onClick={() => this.refreshList()}
                  >

                    <i className="fas fa-sync"></i>

                  </button>
                }

              </div>

            </div>

            <ModelTable
              properties={this.getProperties()}
              getActions={this.listHasActions() ? (entry) => this.getActions(entry) : null}
              data={this.props.complaint_cases}
              initialOrderBy="status"
              initialOrderIsDecrescent={true}
            >

            </ModelTable>

          </DefaultSection>

        </ContentFrame>

      </>
    );
  }
};

const WrappedComplaintCaseList = props => {
  const complaint_cases = useLoaderData();
  const clinics = useContext(ClinicListContext);
  const patients = useContext(PatientListContext);
  const auth = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  return (
    <ComplaintCaseList
      clinics={clinics}
      patients={patients}
      complaint_cases={complaint_cases}
      auth={auth}
      searchParams={searchParams}
      setSearchParams={setSearchParams}
      {...props}
    />
  );
};

export {WrappedComplaintCaseList as ComplaintCaseList};
